import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import {
  sendWaiver,
  signWaiverAtDesk,
  simulateWaiverSigned,
} from '@/domain/waiverFlow';
import { customerName } from '@/utils/format';
import { waiverTone } from '@/utils/statusTone';
import WaiverAgreementForm from '@/components/WaiverAgreementForm';
import type { Booking, WaiverAnswers, WaiverChannel } from '@/domain/types';
import { X, Send, MessageSquare, Mail, Smartphone, ShieldCheck, Camera, ExternalLink } from 'lucide-react';
import clsx from 'clsx';
import { currentActorId } from '@/domain/actor';

interface Props {
  open: boolean;
  booking: Booking;
  onClose: () => void;
}

type SendChannel = Extract<WaiverChannel, 'sms' | 'email'>;

const SEND_CHANNELS: Array<{ value: SendChannel; label: string; Icon: typeof MessageSquare }> = [
  { value: 'sms', label: 'SMS', Icon: MessageSquare },
  { value: 'email', label: 'Email', Icon: Mail },
];

export default function WaiverModal({ open, booking, onClose }: Props) {
  const waivers = useBrainStore((s) => s.waivers);
  const customer = useBrainStore((s) => s.customers[booking.customerId]);
  const pushNotification = useUIStore((s) => s.pushNotification);

  const waiver = useMemo(
    () =>
      Object.values(waivers)
        .filter((w) => w.bookingId === booking.id)
        .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0],
    [waivers, booking.id],
  );

  const [channel, setChannel] = useState<SendChannel>(
    waiver?.sendChannel === 'email' ? 'email' : 'sms',
  );
  const [deskMode, setDeskMode] = useState(false);

  if (!customer) return null;

  const signed = waiver?.status === 'signed';
  const tone = waiverTone(waiver?.status ?? 'not-sent');

  const onSend = () => {
    const isResend = Boolean(waiver && waiver.status !== 'not-sent');
    sendWaiver({ bookingId: booking.id, channel, resend: isResend });
    pushNotification({
      intensity: 'info',
      title: isResend ? 'Waiver resent' : 'Waiver sent',
      body: `Medical waiver sent to ${customer.firstName} on ${channel === 'email' ? 'email' : 'SMS'}. Watch the conversation for the signature.`,
      ttlMs: 4000,
      customerId: customer.id,
    });
  };

  const onSimulateSign = () => {
    if (!waiver) return;
    pushNotification({
      intensity: 'info',
      title: 'Opening on customer device…',
      body: `${customer.firstName} is filling out the waiver.`,
      ttlMs: 2000,
      customerId: customer.id,
    });
    const waiverId = waiver.id;
    setTimeout(() => simulateWaiverSigned(waiverId), 1400);
    onClose();
  };

  const onDeskSigned = (answers: WaiverAnswers) => {
    signWaiverAtDesk({ bookingId: booking.id, answers, by: currentActorId() ?? 'front-desk' });
    pushNotification({
      intensity: 'high',
      title: 'Waiver signed at desk',
      body: `Medical waiver completed for ${customer.firstName}.`,
      ttlMs: 4000,
      customerId: customer.id,
    });
    setDeskMode(false);
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/70 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={onClose}
          data-testid="waiver-modal"
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className="panel bg-ink-deep px-6 py-5 w-full max-w-xl shadow-gold-glow max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="flex items-start justify-between mb-4">
              <div>
                <p className="label-small-caps text-gold-bright">Pre-session waiver</p>
                <h2 className="heading-display text-2xl text-cream mt-1">
                  {customerName(customer)}
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className={clsx('rounded-full border px-2.5 py-1 text-[11px]', tone.border, tone.fill, tone.text)}
                  data-testid="waiver-modal-status"
                >
                  {tone.label}
                </span>
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-quiet"
                  aria-label="Close"
                  data-testid="waiver-modal-close"
                >
                  <X size={14} />
                </button>
              </div>
            </header>

            {signed && waiver ? (
              <SignedSummary waiver={waiver} />
            ) : deskMode ? (
              <WaiverAgreementForm
                customer={customer}
                booking={booking}
                variant="desk"
                onSigned={onDeskSigned}
              />
            ) : (
              <>
                <section>
                  <p className="label-small-caps mb-2">Send link</p>
                  <div className="flex flex-wrap items-center gap-2">
                    {SEND_CHANNELS.map((opt) => {
                      const active = channel === opt.value;
                      const Icon = opt.Icon;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setChannel(opt.value)}
                          data-testid={`waiver-channel-${opt.value}`}
                          className={clsx(
                            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition',
                            active
                              ? 'border-gold-bright bg-gold/10 text-gold-bright'
                              : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                          )}
                        >
                          <Icon size={12} /> {opt.label}
                        </button>
                      );
                    })}
                    <button
                      type="button"
                      onClick={onSend}
                      className="btn-primary text-xs ml-auto"
                      data-testid="waiver-send"
                    >
                      <Send size={12} /> {waiver && waiver.status !== 'not-sent' ? 'Resend' : 'Send'} waiver
                    </button>
                  </div>
                  {waiver && waiver.status === 'sent' ? (
                    <button
                      type="button"
                      onClick={onSimulateSign}
                      className="btn-quiet text-xs mt-3 w-full justify-center"
                      data-testid="waiver-simulate-sign"
                    >
                      <Smartphone size={12} /> Simulate customer signing
                    </button>
                  ) : null}
                  {waiver ? (
                    <a
                      href={`/customer-waiver/${waiver.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-quiet text-xs mt-2 w-full justify-center"
                      data-testid="waiver-customer-link"
                      title="The link the customer receives — opens the public signing page"
                    >
                      <ExternalLink size={12} /> Open customer sign page
                    </a>
                  ) : null}
                </section>

                <div className="flex items-center gap-3 my-5">
                  <span className="h-px flex-1 bg-gold-dim/20" />
                  <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
                    or sign at the desk
                  </span>
                  <span className="h-px flex-1 bg-gold-dim/20" />
                </div>

                <button
                  type="button"
                  onClick={() => setDeskMode(true)}
                  className="btn-ghost text-xs w-full justify-center"
                  data-testid="waiver-open-desk-form"
                >
                  <ShieldCheck size={12} /> Fill in &amp; sign at the desk
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function SignedSummary({ waiver }: { waiver: NonNullable<ReturnType<typeof useBrainStore.getState>['waivers'][string]> }) {
  const a = waiver.answers;
  return (
    <section className="panel-dark p-4" data-testid="waiver-answers">
      <p className="font-inter text-[10px] uppercase tracking-widest text-mint flex items-center gap-1">
        <ShieldCheck size={11} /> Signed{' '}
        {waiver.signedAt
          ? new Date(waiver.signedAt).toLocaleString('en-AU', {
              weekday: 'short',
              day: 'numeric',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })
          : ''}{' '}
        · {waiver.sendChannel === 'manual' ? 'at desk' : waiver.sendChannel}
        {waiver.documentVersion ? ` · ${waiver.documentVersion}` : ''}
      </p>
      <dl className="mt-3 grid grid-cols-1 gap-2 font-serif text-sm text-cream">
        <AnswerRow label="Allergies" values={a?.allergies} />
        <AnswerRow label="Medical conditions" values={a?.conditions} />
        <AnswerRow label="Medications" values={a?.medications} />
        <div className="flex flex-wrap items-center gap-4 pt-1 text-[12px] text-cream-soft">
          <span className="inline-flex items-center gap-1">
            <ShieldCheck size={11} className="text-mint" /> 18+ confirmed
          </span>
          <span className="inline-flex items-center gap-1">
            <ShieldCheck size={11} className="text-mint" /> All clauses agreed
          </span>
          <span className="inline-flex items-center gap-1">
            <Camera size={11} className={a?.photoConsent === false ? 'text-cream-faint' : 'text-mint'} />
            {a?.photoConsent === false ? 'No photos' : 'Photos OK'}
          </span>
        </div>
        {a?.signatureName ? (
          <div className="pt-2 mt-1 border-t border-gold-dim/15">
            <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
              Signature
            </span>
            <p className="font-serif text-lg text-cream italic mt-0.5">{a.signatureName}</p>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

function AnswerRow({ label, values }: { label: string; values?: string[] }) {
  return (
    <div className="flex gap-2">
      <dt className="font-inter text-[11px] uppercase tracking-widest text-cream-faint w-40 shrink-0 pt-0.5">
        {label}
      </dt>
      <dd className={clsx('flex-1', values && values.length ? 'text-cream' : 'text-cream-faint')}>
        {values && values.length ? values.join(', ') : 'None reported'}
      </dd>
    </div>
  );
}
